import React from 'react';

import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import CirclesRating, {
  FilledCircleColor
} from '../../../circles-rating/circles-rating';
import StoppingDistanceChart from '../../../stopping-distance-chart/stopping-distance-chart';

import styles from './tire-comparison.scss';

interface TireComparisonProps {
  currentDistance: number;
  currentRating?: number;
  currentWornDistance?: number;
  isDry?: boolean;
  isLT?: boolean;
  isWet?: boolean;
  recommendedDistance: number;
  recommendedProductLabel: string;
  recommendedProductUrl: string;
  recommendedRating?: number;
  recommendedWornDistance?: number;
}

function TireComparison(props: TireComparisonProps) {
  const {
    currentDistance,
    currentRating,
    currentWornDistance,
    isDry,
    isLT,
    isWet,
    recommendedDistance,
    recommendedProductLabel,
    recommendedProductUrl,
    recommendedRating,
    recommendedWornDistance
  } = props;
  const scaleMax = isLT ? 350 : 250;
  const scaleMin = 0;

  const renderBaseline = () => (
    <>
      <div className={styles['chart-baseline']} />
      <div className={styles['chart-baseline-overflow']} />
    </>
  );

  const renderTicks = (_, scaleMax, scaleMin) => (
    <>
      {renderBaseline()}
      <span className={styles.tick} style={{ right: '-2px' }}>
        {scaleMax}
      </span>
      <span className={styles.tick}>{scaleMin}</span>
    </>
  );

  return (
    <div>
      {isDry && (
        <>
          <Typography size="small" styleName="heading">
            Current tire
            {typeof currentRating === 'number' && (
              <div styleName="circles-rating-container">
                <CirclesRating
                  filledCircleColor={FilledCircleColor.GOLD}
                  rating={Number(currentRating)}
                />
                {currentRating.toFixed(1)}
              </div>
            )}
          </Typography>
          <div styleName="chart-row-wrap">
            <div styleName="chart-row__dry-tread">
              <StoppingDistanceChart
                renderTicks={renderTicks}
                scaleMax={scaleMax}
                scaleMin={scaleMin}
                styleName="chart"
                value={currentDistance}
              />
            </div>
          </div>
          {Boolean(recommendedDistance && recommendedRating) && (
            <>
              <Typography size="small" styleName="recommended-heading">
                <div>
                  Best tire
                  {Boolean(recommendedProductUrl) && (
                    <>
                      {' - '}
                      <Link styleName="link" to={recommendedProductUrl}>
                        {recommendedProductLabel}
                      </Link>
                    </>
                  )}
                </div>
                {typeof recommendedRating === 'number' && (
                  <div styleName="circles-rating-container">
                    <CirclesRating
                      filledCircleColor={FilledCircleColor.GOLD}
                      rating={recommendedRating}
                    />
                    {recommendedRating.toFixed(1)}
                  </div>
                )}
              </Typography>
              <div styleName="chart-row-wrap">
                <div styleName="chart-row__dry-tread">
                  <StoppingDistanceChart
                    renderTicks={renderTicks}
                    scaleMax={scaleMax}
                    scaleMin={scaleMin}
                    styleName="chart"
                    value={recommendedDistance}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {isWet && (
        <>
          <Typography size="small" styleName="heading">
            Current tire
            {typeof currentRating === 'number' && (
              <div styleName="circles-rating-container">
                <CirclesRating
                  filledCircleColor={FilledCircleColor.BLUE}
                  rating={currentRating}
                />
                {currentRating.toFixed(1)}
              </div>
            )}
          </Typography>
          <div styleName="chart-row-wrap">
            <div styleName="chart-row__new-tread">
              <span styleName="wet-tread-type">New</span>
              <StoppingDistanceChart
                renderTicks={renderBaseline}
                scaleMax={scaleMax}
                scaleMin={scaleMin}
                styleName="chart"
                value={currentDistance}
              />
            </div>
            {Number(currentWornDistance) > 0 && (
              <div styleName="chart-row__worn-tread">
                <span styleName="wet-tread-type">Worn</span>
                <StoppingDistanceChart
                  renderTicks={renderTicks}
                  scaleMax={scaleMax}
                  scaleMin={scaleMin}
                  styleName="chart"
                  value={Number(currentWornDistance)}
                />
              </div>
            )}
          </div>
          {Boolean(
            recommendedDistance && recommendedRating && recommendedWornDistance
          ) && (
            <>
              <Typography size="small" styleName="recommended-heading">
                <div>
                  Best tire
                  {Boolean(recommendedProductUrl) && (
                    <>
                      {' - '}
                      <Link styleName="link" to={recommendedProductUrl}>
                        {recommendedProductLabel}
                      </Link>
                    </>
                  )}
                </div>
                {typeof recommendedRating === 'number' && (
                  <div styleName="circles-rating-container">
                    <CirclesRating
                      filledCircleColor={FilledCircleColor.BLUE}
                      rating={recommendedRating}
                    />
                    {recommendedRating.toFixed(1)}
                  </div>
                )}
              </Typography>
              <div styleName="chart-row-wrap">
                <div styleName="chart-row__new-tread">
                  <span styleName="wet-tread-type">New</span>
                  <StoppingDistanceChart
                    renderTicks={renderBaseline}
                    scaleMax={scaleMax}
                    scaleMin={scaleMin}
                    styleName="chart"
                    value={recommendedDistance}
                  />
                </div>
                {Number(recommendedWornDistance) > 0 && (
                  <div styleName="chart-row__worn-tread">
                    <span styleName="wet-tread-type">Worn</span>
                    <StoppingDistanceChart
                      renderTicks={renderTicks}
                      scaleMax={scaleMax}
                      scaleMin={scaleMin}
                      styleName="chart"
                      value={Number(recommendedWornDistance)}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default TireComparison;
